/* eslint-disable react/jsx-key */
import { Fragment } from 'react';
import UserContext from '../context/context';
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from '@wordpress/element';
import { addQueryArgs } from '@wordpress/url';
import { Button, Dropdown } from 'semantic-ui-react';

const FEATURE_POST_SLUG = 'political-typology-comparison-2026';

/**
 * Resolve the selected issue slug from the current URL or server-injected prcURLVars.
 *
 * Prefers explicit query (?prcFeatureUrlParamA=), then pathname segment after the feature slug
 * (pretty permalinks), then initial prcFeatureUrlVars from the loader inline script.
 *
 * @return {string|null} Resolved child slug when the URL specifies an issue; otherwise null.
 */
function getIssueSlugFromLocation() {
	const fromQuery = new URLSearchParams(window.location.search).get(
		'prcFeatureUrlParamA'
	);
	if (fromQuery) {
		return fromQuery.trim();
	}

	const parts = window.location.pathname
		.replace(/\/$/, '')
		.split('/')
		.filter(Boolean);
	const idx = parts.lastIndexOf(FEATURE_POST_SLUG);
	if (idx !== -1 && parts[idx + 1]) {
		return parts[idx + 1].trim();
	}

	if (
		typeof window.prcURLVars !== 'undefined' &&
		window.prcURLVars.prcFeatureUrlParamA
	) {
		return String(window.prcURLVars.prcFeatureUrlParamA).trim();
	}

	return null;
}

const MobileMenu = () => {
	const {
		issues,
		selectedChild,
		selectedView,
		updateChild,
		updateView,
		selectChildBySlug,
	} = useContext(UserContext);

	const filteredIssues = useMemo(() => {
		if (selectedView === 'demographics') {
			return issues.filter(
				(issue) => issue.parent_slug === 'demographics'
			);
		}
		return issues.filter((issue) => issue.parent_slug !== 'demographics');
	}, [issues, selectedView]);

	const updateURL = (slug) => {
		const url = addQueryArgs(window.location.href, {
			prcFeatureUrlParamA: slug,
		});
		window.history.pushState({ ...window.history.state, slug }, '', url);
	};

	const updateChildItem = useCallback(
		(child) => {
			updateChild(child);
			updateURL(child.slug);
		},
		[updateChild]
	);

	useEffect(() => {
		const currentIssue = issues.find((issue) =>
			issue.children.some((c) => c.slug === selectedChild.slug)
		);
		const stillVisible =
			currentIssue && filteredIssues.includes(currentIssue);
		if (stillVisible) {
			return;
		}
		const first = filteredIssues[0];
		if (first?.children[0]) {
			updateChildItem(first.children[0]);
		}
	}, [filteredIssues, issues, selectedChild, updateChildItem]);

	const applySlugToMenu = useCallback(
		(slug) => {
			selectChildBySlug(slug);
		},
		[selectChildBySlug]
	);

	useEffect(() => {
		const slug = getIssueSlugFromLocation();
		if (!slug) {
			return;
		}
		applySlugToMenu(slug);
	}, [applySlugToMenu]);

	useEffect(() => {
		const onPopState = () => {
			const slug = getIssueSlugFromLocation();
			if (!slug) {
				return;
			}
			applySlugToMenu(slug);
		};

		window.addEventListener('popstate', onPopState);
		return () => window.removeEventListener('popstate', onPopState);
	}, [applySlugToMenu]);

	return (
		<Fragment>
			<h4 className="accordion-menu-title"> BROWSE BY: </h4>
			<Button.Group basic>
				<Button
					basic
					active={selectedView === 'key topics'}
					onClick={() => updateView('key topics')}
				>
					KEY TOPICS
				</Button>
				<Button
					basic
					active={selectedView === 'demographics'}
					onClick={() => updateView('demographics')}
				>
					DEMOGRAPHICS
				</Button>
			</Button.Group>

			<p className="mobile-menu-dropdown-title">View comparisons by:</p>
			<Dropdown
				className="mobile-menu-dropdown"
				fluid
				selection
				text={selectedChild?.name || 'Select a topic'}
			>
				<Dropdown.Menu>
					{filteredIssues.map((issue, i) => (
						<Fragment key={`${issue.parent_slug}-${i}`}>
							<Dropdown.Header content={issue.parent} />
							{issue.children.map((child) => (
								<Dropdown.Item
									key={child.slug}
									text={child.name}
									active={selectedChild.slug === child.slug}
									selected={selectedChild.slug === child.slug}
									onClick={() => updateChildItem(child)}
								/>
							))}
						</Fragment>
					))}
				</Dropdown.Menu>
			</Dropdown>
		</Fragment>
	);
};

export default MobileMenu;
